import React, { useState, useEffect } from 'react';
import ReactSelect from 'react-select';
import { motion, AnimatePresence } from 'framer-motion';

const MotionMenu = ({ children, innerRef, innerProps, selectProps, className, ...rest }) => {
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (selectProps.menuIsOpen) setShouldRender(true);
  }, [selectProps.menuIsOpen]);

  return (
    <AnimatePresence
      onExitComplete={() => {
        if (!selectProps.menuIsOpen) setShouldRender(false);
      }}
    >
      {shouldRender && (
        <motion.div
          ref={innerRef}
          {...innerProps}
          className={className}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          {...rest}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Select = ({
  id,
  name,
  label,
  required = false,
  placeholder = 'Selecione',
  value,
  onChange,
  options = [],
  className = '',
  size = 'sm',
  error = false,
}) => {
  const selectedOption = options.find((option) => option.value === value) || null;
  const [isFocused, setIsFocused] = useState(false);

  const sizeClasses = {
    xs: {
      height: '2.25rem',
      fontSize: '0.875rem',
      paddingLeft: '0.5rem',
      paddingTop: '0.5rem',
      paddingBottom: '0.125rem',
      borderRadius: '0.875rem 0.875rem 0.875rem 0rem',
    },
    sm: {
      height: '2.625rem',
      fontSize: '1rem',
      paddingLeft: '0.75rem',
      paddingTop: '1rem',
      paddingBottom: '0.25rem',
      borderRadius: '0.875rem 0.875rem 0.875rem 0rem',
    },
    md: {
      height: '3rem',
      fontSize: '1rem',
      paddingLeft: '0.75rem',
      paddingTop: '0.75rem',
      paddingBottom: '0.75rem',
      borderRadius: '1rem 1rem 1rem 0rem',
    },
    lg: {
      height: '3.8rem',
      fontSize: '1.125rem',
      paddingLeft: '1rem',
      paddingTop: '1.2rem',
      paddingBottom: '0.9rem',
      borderRadius: '1.25rem 1.25rem 1.25rem 0rem',
    },
  };
  

  const styleSize = sizeClasses[size] || sizeClasses.sm;

  return (
    <div className={`relative w-full mb-5 ${className}`}>
      {label && (
        <label
          htmlFor={id}
          className={`
            absolute text-sm left-4 bg-white px-1 transition-all pointer-events-none z-10
            ${
              selectedOption || isFocused
                ? `top-[-0.5rem] text-sm ${
                    error ? 'text-red-500' : 'text-purple-700'
                  }`
                : 'top-1/2 -translate-y-1/2 text-gray-500'
            }
          `}
        >
          {required && '*\u00A0'}
          {label}
        </label>
      )}

      <ReactSelect
        inputId={id}
        name={name}
        value={selectedOption}
        onChange={(selectedOption) =>
          onChange({
            target: {
              name,
              value: selectedOption?.value || '',
            },
          })
        }
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        options={options}
        components={{ Menu: MotionMenu }}
        placeholder=" "
        menuPosition="fixed"
        menuPortalTarget={document.body}
        styles={{
          control: (base, state) => ({
            ...base,
            fontSize: styleSize.fontSize,
            minHeight: styleSize.height,
            height: styleSize.height,
            paddingLeft: styleSize.paddingLeft,
            paddingRight: '0.5rem',
            borderRadius: styleSize.borderRadius,
            borderColor: error ? '#ef4444' : '#d1d5db',
            boxShadow: state.isFocused
              ? `0 0 0 1px ${error ? '#ef4444' : 'rgba(168, 85, 247, 0.25)'}`
              : 'none',
            '&:hover': {
              borderColor: state.isFocused
                ? error ? '#ef4444' : '#6b21a8'
                : error ? '#ef4444' : '#d1d5db',
            },
          }),
          input: (base) => ({
            ...base,
            margin: 0,
            padding: 0,
          }),
          placeholder: (base) => ({
            ...base,
            fontWeight: '300',
            fontSize: styleSize.fontSize,
            color: 'transparent',
          }),
          singleValue: (base) => ({
            ...base,
            fontSize: styleSize.fontSize,
            paddingTop: 0,
            paddingBottom: 0,
          }),
          menu: (base) => ({
            ...base,
            borderRadius: '0.75rem',
            overflow: 'hidden',
            boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
            backgroundColor: 'white',
          }),
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
            backgroundColor: 'white',
          }),
          option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected
              ? '#b377e8'
              : state.isFocused
              ? '#ede9fe'
              : 'white',
            color: state.isSelected
              ? 'white'
              : state.isFocused
              ? '#6b21a8'
              : '#6b7280',
            cursor: 'pointer',
            ':active': {
              backgroundColor: '#ede9fe',
              color: '#6b21a8',
            },
          }),
        }}
      />
    </div>
  );
};

export default Select;
